/**
 * Packs: selecting findings to publish, and the versions already published.
 *
 * A published version is bytes, not a query. Rendering happens once at publish and the zip
 * is stored, because a subscriber who pulled a version must get that file forever —
 * rebuilding on request would let a later edit rewrite history under them.
 */

import type { JSX } from 'react'

/**
 * The study workspace: the cards a reader works their own corpus in.
 *
 * Separate from `cards.tsx`, which is the *text* — passages, evidence, the
 * lexicon. That file is what the product is for; this one is where the reader
 * keeps what they found. They share the output window and its stack, because a
 * finding is a passage-shaped thing and belongs where passages live, but they
 * are different jobs and a single thousand-line file would hide that.
 *
 * Two rules from the design shape everything here:
 *
 * * **Cards are data, never closures.** Every card in the stack is a plain
 *   value — `{ kind: 'finding', id }`, never `{ onSave: () => … }` — so the
 *   stack stays serialisable and navigation is not coupled to whoever pushed
 *   it. Anything a card needs from React state arrives as a prop on the window.
 * * **Filtering, sorting and paging happen on the server.** A corpus grows
 *   without limit; a page that ships every row and hides most of them gets
 *   slower every time the reader does good work. Sorting one page in the
 *   browser would be worse still — it would reorder the window while claiming
 *   to have sorted the list.
 */

import { useEffect, useState } from 'react'
import type {
  FindingSummary,
  PackList,
  PackSummary,
} from '../account'
import {
  createPack,
  deletePack,
  feedUrl,
  listFindings,
  listPacks,
  packArchiveUrl,
  publishPack,
  readPack,
  updatePack,
} from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

import { Failure, Loading, useLoaded } from './cardDisplay'
import { FormActions, RecordConflict, useRecordEditor } from './recordEditing'

// ---------------------------------------------------------------------------
// Packs
// ---------------------------------------------------------------------------

export function PacksCard({
  corpus,
  rootOrigin,
  onOpen,
}: {
  corpus: string | null
  rootOrigin: string
  onOpen: (card: Card) => void
}): JSX.Element {
  const { state, reload } = useLoaded<PackList>(
    () => listPacks(corpus ?? undefined),
    `packs:${corpus}`,
  )

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  const { packs, author_slug, has_publishing_addon } = state.data

  return (
    <article>
      <header className="card__head">
        <h2 className="card__kind">{t('packs.title')}</h2>
        <button type="button" className="link" onClick={() => onOpen({ kind: 'packEditor', id: null })}>
          {t('packs.new')}
        </button>
      </header>

      <p className="muted">{t('packs.note')}</p>

      {!author_slug && <AccountNeeded message={t('packs.needsName')} rootOrigin={rootOrigin} />}
      {!has_publishing_addon && <AccountNeeded message={t('packs.needsAddon')} rootOrigin={rootOrigin} />}

      {author_slug && (
        <p className="muted">
          <FeedAddress authorSlug={author_slug} />
        </p>
      )}

      {packs.length === 0 && <p className="muted">{t('packs.empty')}</p>}

      <ol className="claims">
        {packs.map((pack) => (
          <PackRow
            key={pack.id}
            pack={pack}
            authorSlug={author_slug}
            canPublish={Boolean(author_slug) && has_publishing_addon}
            onOpen={onOpen}
            onChanged={reload}
          />
        ))}
      </ol>
    </article>
  )
}

/**
 * Why Publish is off, said before the click, with the way to fix it: both causes — no
 * publishing name, no Feed publishing add-on — are settled on the account page at the root.
 */
function AccountNeeded({ message, rootOrigin }: { message: string; rootOrigin: string }): JSX.Element {
  return (
    <p className="failure" role="alert">
      {message}{' '}
      <a className="link" href={`${rootOrigin}/account.html`}>
        {t('account.manage')}
      </a>
    </p>
  )
}

/**
 * The address a subscriber points at — absolute, because its whole purpose is
 * to be sent to somebody else. A relative path is useless the moment it leaves
 * the page, and this line showed one for a while.
 */
function FeedAddress({ authorSlug }: { authorSlug: string }): JSX.Element {
  return (
    <>
      {t('packs.feedIs')}{' '}
      <code>
        {window.location.origin}
        {feedUrl(authorSlug)}
      </code>
      <br />
      <small>{t('packs.feedShare')}</small>
    </>
  )
}

/**
 * The published file itself.
 *
 * Publishing rendered bytes and returned an address, and neither was reachable
 * from this page — so a reader could publish a pack and still have nothing to
 * hand anybody.
 */
function DownloadLink({
  pack,
  authorSlug,
}: {
  pack: PackSummary
  authorSlug: string | null
}): JSX.Element | null {
  if (!authorSlug || !pack.latest_version) return null
  return (
    <p className="claim__meta">
      <a
        className="link"
        href={packArchiveUrl(authorSlug, pack.slug, pack.latest_version.version)}
        download
      >
        {t('packs.download', { version: pack.latest_version.version })}
      </a>
    </p>
  )
}

/**
 * Publish, and delete.
 *
 * Publishing is disabled without a publishing identity: the author slug is the folder every
 * subscriber unpacks into, so a pack published without one has nowhere to land. It is also
 * disabled without the Feed publishing add-on, which the server enforces either way.
 */
function PackActions({
  pack,
  canPublish,
  busy,
  onPublish,
  onChanged,
}: {
  pack: PackSummary
  canPublish: boolean
  busy: boolean
  onPublish: () => void
  onChanged: () => void
}): JSX.Element {
  return (
    <p>
      <button type="button" className="link" disabled={!canPublish || busy} onClick={onPublish}>
        {t('packs.publish')}
      </button>
      <button
        type="button"
        className="link"
        onClick={async () => {
          if (!window.confirm(t('packs.confirmDelete'))) return
          await deletePack(pack.id)
          onChanged()
        }}
      >
        {t('findings.delete')}
      </button>
    </p>
  )
}

function PackRow({
  pack,
  authorSlug,
  canPublish,
  onOpen,
  onChanged,
}: {
  pack: PackSummary
  authorSlug: string | null
  canPublish: boolean
  onOpen: (card: Card) => void
  onChanged: () => void
}) {
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function publish() {
    // Publishing is the moment private study becomes a public file. Saying so
    // in plain words is the whole of the consent: after this, anyone with the
    // address has these findings.
    if (!window.confirm(t('packs.confirmPublish', { count: pack.findings.length }))) return
    setBusy(true)
    setFailure(null)
    try {
      await publishPack(pack.id)
      onChanged()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="claim">
      <p className="claim__text">
        <button type="button" className="link" onClick={() => onOpen({ kind: 'packEditor', id: pack.id })}>
          {pack.title}
        </button>
      </p>
      <p className="claim__meta muted">
        {t('packs.count', { count: pack.findings.length })} · {pack.license}
        {pack.latest_version ? (
          <span> · {t('packs.published', { version: pack.latest_version.version })}</span>
        ) : (
          <span> · {t('packs.unpublished')}</span>
        )}
      </p>
      <DownloadLink pack={pack} authorSlug={authorSlug} />
      <PackActions
        pack={pack}
        canPublish={canPublish}
        busy={busy}
        onPublish={() => void publish()}
        onChanged={onChanged}
      />
      {failure && <Failure message={failure} />}
    </li>
  )
}

/** A pack as its form holds it: how it is described, how it is licensed, and what is in it. */
interface PackForm {
  title: string
  description: string
  license: string
  homepage: string
  findings: string[]
}

const BLANK_PACK: PackForm = {
  title: '',
  description: '',
  license: 'CC BY 4.0',
  homepage: '',
  findings: [],
}

/** The form's strings as the fields the server takes. */
function toPackFields(form: PackForm) {
  return {
    title: form.title,
    description: form.description,
    license_name: form.license,
    // An absent homepage is `null`, not `''`: the feed renders a link only when there is
    // one, and a blank string is an address that goes nowhere.
    homepage: form.homepage || null,
    findings: form.findings,
  }
}

/**
 * The findings a pack may contain: the reader's own, and only those.
 *
 * An imported finding shown here would be a choice the server refuses — membership is
 * filtered for own-findings when it is set and again at publish, and offering one in the
 * picker would just move the refusal later.
 */
function useOwnFindings(corpus: string | null): FindingSummary[] {
  const [available, setAvailable] = useState<FindingSummary[]>([])

  useEffect(() => {
    listFindings({
      corpus: corpus ?? undefined,
      imported_from: '',
      limit: 200,
      sort: 'title',
      direction: 'asc',
    }).then(
      (page) => setAvailable(page.findings),
      () => setAvailable([]),
    )
  }, [corpus])

  return available
}

/** Which findings this pack ships. Checked membership, not an ordering. */
function MembershipPicker({
  available,
  chosen,
  onChange,
}: {
  available: FindingSummary[]
  chosen: string[]
  onChange: (findings: string[]) => void
}): JSX.Element {
  return (
    <fieldset className="picker">
      <legend>{t('packs.choose')}</legend>
      {available.map((finding) => (
        <label key={finding.id} className="picker__row">
          <input
            type="checkbox"
            checked={chosen.includes(finding.slug)}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...chosen, finding.slug]
                  : chosen.filter((slug) => slug !== finding.slug),
              )
            }
          />
          <span>{finding.title}</span>
        </label>
      ))}
      {available.length === 0 && <p className="muted">{t('findings.empty')}</p>}
    </fieldset>
  )
}

/** How the pack presents itself to a subscriber: what it is, and on what terms. */
function PackDetailFields({
  fields,
  set,
}: {
  fields: PackForm
  set: (patch: Partial<PackForm>) => void
}): JSX.Element {
  return (
    <>
      <label>
        {t('editor.title')}
        <input
          value={fields.title}
          onChange={(event) => set({ title: event.target.value })}
          required
        />
      </label>
      <label>
        {t('packs.description')}
        <textarea
          value={fields.description}
          onChange={(event) => set({ description: event.target.value })}
          rows={4}
        />
      </label>
      <div className="form__row">
        <label>
          {t('packs.license')}
          <input value={fields.license} onChange={(event) => set({ license: event.target.value })} />
        </label>
        <label>
          {t('packs.homepage')}
          <input
            value={fields.homepage}
            onChange={(event) => set({ homepage: event.target.value })}
          />
        </label>
      </div>
    </>
  )
}

export function PackEditorCard({
  id,
  corpus,
  consented,
  onBack,
}: {
  id: number | null
  corpus: string | null
  consented: boolean
  onBack: () => void
}): JSX.Element {
  const available = useOwnFindings(corpus)
  const editor = useRecordEditor<PackSummary, PackForm>({
    id,
    blank: BLANK_PACK,
    read: readPack,
    toForm: (pack) => ({
      title: pack.title,
      description: pack.description,
      license: pack.license,
      homepage: pack.homepage ?? '',
      findings: pack.findings,
    }),
    create: (form) =>
      createPack({ ...toPackFields(form), corpus: corpus ?? undefined, consent: !consented }),
    update: (packId, form, rowVersion) =>
      updatePack(packId, { ...toPackFields(form), row_version: rowVersion }),
    onBack,
  })
  const { fields, set, failure, ready, save } = editor

  if (!ready) return failure ? <Failure message={failure} /> : <Loading />

  return (
    <article>
      <h2 className="card__kind">{id === null ? t('packs.new') : t('packs.edit')}</h2>
      <form className="form" onSubmit={save}>
        <PackDetailFields fields={fields} set={set} />

        <MembershipPicker
          available={available}
          chosen={fields.findings}
          onChange={(findings) => set({ findings })}
        />

        {failure && <Failure message={failure} />}
        <RecordConflict
          editor={editor}
          labels={{
            title: t('editor.title'),
            description: t('packs.description'),
            license: t('packs.license'),
            homepage: t('packs.homepage'),
            findings: t('packs.choose'),
          }}
        />
        <FormActions onCancel={onBack} />
      </form>
    </article>
  )
}
